/**
 * 通用日历日期选择器（CLAUDE.md §前端规范：组件 PascalCase、单默认导出、禁内联样式）。
 *
 * 已读 leo-frontend-design.md v1.0，适用章节：§6.3 表单控件（触发钮复用
 * leo-select 控件视觉）、§6.2 选中态（橙 tint 底 + 橙字）、§6.8 内联 SVG 图标、
 * §7.1 过渡（0.15s ease）、§7.3 日期格式（YYYY-MM-DD）。
 *
 * 交互：点击触发钮展开月历面板 → 点选「白名单内」的日期 → onChange 并收起；
 * 点击面板外 / Esc 收起。白名单外日期禁用（灰化、不可点）——用于
 * 「仅表内有数据的日期可选」类筛选（如 T0广告看板·天日期）。
 * 月份导航范围钳制在白名单最小 / 最大日期所在月份之间；
 * 每次展开时视图重置到 选中值 > 白名单最大 > 今日 的月份。
 */

import { useEffect, useMemo, useRef, useState } from "react";

interface DateCalendarPickerProps {
  /** 当前选中日期（yyyy-MM-dd；空串 = 未选）。 */
  value: string;
  /** 可选日期白名单（yyyy-MM-dd 清单；不在清单内的日期禁用）。 */
  allowedDates: string[];
  /** 选中回调（仅白名单内日期被点选时触发）。 */
  onChange: (ds: string) => void;
  /** 禁用整体（选项未加载等场景）。 */
  disabled?: boolean;
}

/** 星期头（周一开头，中文站惯例）。 */
const WEEKDAYS = ["一", "二", "三", "四", "五", "六", "日"];

/** 零填充两位。 */
function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** 构造 yyyy-MM-dd（本地时区；避免 toISOString 的 UTC 偏移坑）。 */
function toDs(y: number, month0: number, d: number): string {
  return `${y}-${pad2(month0 + 1)}-${pad2(d)}`;
}

/** 今日 yyyy-MM-dd（本地时区）。 */
function todayDs(): string {
  const now = new Date();
  return toDs(now.getFullYear(), now.getMonth(), now.getDate());
}

/** 生成某月日历格子：周一开头、月前补 null 占位；返回 天日期 | null 序列。 */
function buildMonthCells(year: number, month0: number): (string | null)[] {
  const first = new Date(year, month0, 1);
  const daysInMonth = new Date(year, month0 + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // 周日(0) → 6，周一(1) → 0
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(toDs(year, month0, d));
  return cells;
}

/** 月份游标平移（跨年由 Date 归一化）。 */
function shiftMonth(y: number, month0: number, delta: number): [number, number] {
  const d = new Date(y, month0 + delta, 1);
  return [d.getFullYear(), d.getMonth()];
}

export default function DateCalendarPicker({
  value,
  allowedDates,
  onChange,
  disabled = false,
}: DateCalendarPickerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  /** 白名单升序副本（取最小/最大月界）与集合（O(1) 判定可点）。 */
  const sortedAllowed = useMemo(() => [...allowedDates].sort(), [allowedDates]);
  const allowedSet = useMemo(() => new Set(allowedDates), [allowedDates]);
  const minDs = sortedAllowed[0] ?? "";
  const maxDs = sortedAllowed[sortedAllowed.length - 1] ?? "";

  /** 视图月份游标（展开时重置到锚点月：选中值 > 白名单最大 > 今日）。 */
  const [viewY, setViewY] = useState(() =>
    Number((value || maxDs || todayDs()).slice(0, 4)),
  );
  const [viewM0, setViewM0] = useState(() =>
    Number((value || maxDs || todayDs()).slice(5, 7)) - 1,
  );

  // 展开时重置视图到锚点月份（每次打开聚焦当前选中 / 最新有数据月）
  useEffect(() => {
    if (!open) return;
    const anchor = value || maxDs || todayDs();
    setViewY(Number(anchor.slice(0, 4)));
    setViewM0(Number(anchor.slice(5, 7)) - 1);
  }, [open, value, maxDs]);

  // 点击面板外 / Esc 收起
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const viewYm = `${viewY}-${pad2(viewM0 + 1)}`;
  /** 月份导航钳制：不越过白名单最小 / 最大日期所在月份。 */
  const canPrev = minDs.slice(0, 7) < viewYm;
  const canNext = viewYm < maxDs.slice(0, 7);
  const cells = buildMonthCells(viewY, viewM0);
  const today = todayDs();

  /** 月份平移（受 canPrev / canNext 钳制）。 */
  function onShiftMonth(delta: number) {
    const [y, m0] = shiftMonth(viewY, viewM0, delta);
    setViewY(y);
    setViewM0(m0);
  }

  /** 日期格样式：白名单外禁用灰化；选中橙 tint + 橙字（LEO §6.2）；
   *  今日（未选中）细环标记；可点日期 hover 浅底。 */
  function dayCellClass(ds: string): string {
    const base =
      "flex h-7 items-center justify-center rounded-md text-[12px] transition-colors";
    if (ds === value) {
      return `${base} bg-[rgba(255,107,53,0.15)] font-semibold text-accent-cta`;
    }
    if (!allowedSet.has(ds)) {
      return `${base} cursor-not-allowed text-text-muted-weak opacity-40`;
    }
    const ring = ds === today ? " ring-1 ring-inset ring-line-strong" : "";
    return `${base} text-text-strong hover:bg-da-inset${ring}`;
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        disabled={disabled || sortedAllowed.length === 0}
        onClick={() => setOpen((v) => !v)}
        className="leo-select flex w-[150px] items-center justify-between gap-2 text-left font-mono"
      >
        <span>{value || (sortedAllowed.length ? "选择日期" : "暂无数据")}</span>
        {/* 日历图标（LEO §6.8 内联 SVG，stroke 1.75） */}
        <svg
          viewBox="0 0 24 24"
          className="h-3.5 w-3.5 shrink-0 text-text-muted"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-[100] mt-1 w-[232px] rounded-lg border border-line-default bg-da-card p-3 shadow-card">
          {/* 月份导航（钳制在白名单月界内） */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label="上一月"
              disabled={!canPrev}
              onClick={() => onShiftMonth(-1)}
              className="leo-btn-ghost px-1.5 py-0.5 disabled:opacity-40"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="text-[12.5px] font-semibold text-text-emphasis">
              {viewY} 年 {viewM0 + 1} 月
            </span>
            <button
              type="button"
              aria-label="下一月"
              disabled={!canNext}
              onClick={() => onShiftMonth(1)}
              className="leo-btn-ghost px-1.5 py-0.5 disabled:opacity-40"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          {/* 星期头（周一开头） */}
          <div className="grid grid-cols-7 gap-0.5 text-center text-[10.5px] text-text-muted-weak">
            {WEEKDAYS.map((w) => (
              <span key={w} className="py-1">
                {w}
              </span>
            ))}
          </div>

          {/* 日期格（月前 null 占位；白名单外禁用） */}
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((ds, i) =>
              ds === null ? (
                <span key={`blank-${i}`} />
              ) : (
                <button
                  key={ds}
                  type="button"
                  disabled={!allowedSet.has(ds)}
                  onClick={() => {
                    onChange(ds);
                    setOpen(false);
                  }}
                  className={dayCellClass(ds)}
                >
                  {Number(ds.slice(8, 10))}
                </button>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}
